import React from "react";

// ─── Small building blocks ─────────────────────────────────────
const Stat = ({ label, value, hint, color = "amber", testId }) => {
  const palette = {
    amber: "text-amber-100 bg-amber-500/10 border-amber-500/20",
    green: "text-green-300 bg-green-500/10 border-green-500/25",
    red: "text-red-300 bg-red-500/10 border-red-500/25",
    blue: "text-blue-300 bg-blue-500/10 border-blue-500/25",
    neutral: "text-neutral-300 bg-neutral-500/10 border-neutral-500/20",
  };
  const cls = palette[color] || palette.amber;
  return (
    <div className={`rounded-lg border px-2.5 py-2 ${cls}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-amber-500/70 leading-none mb-1">{label}</div>
      <div className="text-lg font-bold leading-tight tabular-nums">
        {value === null || value === undefined ? "—" : value}
      </div>
      {hint && <div className="text-[10px] text-amber-500/60 mt-0.5">{hint}</div>}
    </div>
  );
};

const Tag = ({ label, tone = "amber" }) => {
  const palette = {
    amber: "bg-amber-500/15 text-amber-200 border-amber-500/30",
    green: "bg-green-500/15 text-green-200 border-green-500/30",
    red: "bg-red-500/15 text-red-200 border-red-500/30",
    blue: "bg-blue-500/15 text-blue-200 border-blue-500/30",
    neutral: "bg-neutral-500/15 text-neutral-300 border-neutral-500/30",
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-widest font-semibold ${palette[tone] || palette.amber}`}>
      {label}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  if (!status) return null;
  const tone = ["no_data", "not_indexed_yet", "not_available", "insufficient_data", "no_calibration_data", "unknown_team", "unknown_league", "no_shots_data"].includes(status)
    ? "neutral"
    : "green";
  return <Tag label={status.replace(/_/g, " ")} tone={tone} />;
};

const Note = ({ children }) => (
  <div className="text-[11px] text-amber-500/70 italic bg-amber-950/30 border-l-2 border-amber-500/40 px-2.5 py-1.5 rounded-sm">
    {children}
  </div>
);

const Section = ({ title, children }) => (
  <div className="mb-3">
    {title && <div className="text-[10px] uppercase tracking-widest text-amber-500/60 mb-1.5">{title}</div>}
    {children}
  </div>
);

const KVList = ({ items }) => (
  <div className="space-y-1">
    {items.map(([k, v], i) => (
      <div key={i} className="flex justify-between text-[11px] py-0.5 border-b border-amber-500/10">
        <span className="text-amber-500/70 uppercase tracking-wider">{k}</span>
        <span className="text-amber-100 font-mono tabular-nums">
          {v === null || v === undefined ? "—" : String(v)}
        </span>
      </div>
    ))}
  </div>
);

const Table = ({ headers, rows, testId }) => (
  <div className="overflow-x-auto rounded border border-amber-500/15" data-testid={testId}>
    <table className="w-full text-[11px]">
      <thead className="bg-amber-500/10">
        <tr>
          {headers.map((h, i) => (
            <th key={i} className="text-left px-2 py-1.5 text-amber-400/80 uppercase tracking-widest text-[10px] font-semibold">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.slice(0, 15).map((r, i) => (
          <tr key={i} className="odd:bg-black/30 border-t border-amber-500/5">
            {r.map((c, j) => (
              <td key={j} className="px-2 py-1 text-amber-100 tabular-nums whitespace-nowrap">{c === null || c === undefined ? "—" : c}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Pct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`);
const Num = (v, d = 2) => (v === null || v === undefined ? "—" : Number(v).toFixed(d));

// ─── Layer-specific renderers ──────────────────────────────────
const RenderL1 = ({ data }) => {
  const c = data.counts || {};
  const bets = data.betsapi || {};
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <Stat label="Matches Total" value={c.matches_total?.toLocaleString?.() || c.matches_total} />
        <Stat label="Matches Settled" value={c.matches_settled?.toLocaleString?.() || c.matches_settled} color="green" />
        <Stat label="Team ELO Ratings" value={c.team_elo_ratings} />
        <Stat label="Features Hydrated" value={c.match_features_hydrated} color={c.match_features_hydrated > 100 ? "green" : "neutral"} />
      </div>
      <Section title="Data Providers">
        <div className="flex flex-wrap gap-1.5">
          {(data.providers || []).map((p, i) => <Tag key={i} label={p} tone="blue" />)}
        </div>
      </Section>
      {bets.available !== undefined && (
        <Section title="BetsAPI Coverage">
          <div className="flex items-center gap-2 mb-2">
            <Tag label={bets.available ? "ACTIVE" : "OFFLINE"} tone={bets.available ? "green" : "red"} />
            <span className="text-[11px] text-amber-500/70">{bets.auth_status}</span>
          </div>
          <div className="text-[11px] text-amber-100/80">
            Covers <span className="font-bold">{bets.layers_covered?.length || 0}</span> layers directly · derives <span className="font-bold">{bets.layers_derived?.length || 0}</span>
          </div>
        </Section>
      )}
    </div>
  );
};

const RenderL2 = ({ data }) => {
  const g = data.goal_distribution || {};
  const o = data.outcomes || {};
  const m = data.market_frequencies || {};
  const p = data.profile || {};
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="text-xs text-amber-500/70">Sample size:</div>
        <div className="text-sm font-bold text-amber-100 tabular-nums">{data.sample_size}</div>
      </div>
      <Section title="Goal Distribution">
        <div className="grid grid-cols-3 gap-2">
          <Stat label="Total goals" value={Num(g.avg_total_goals)} />
          <Stat label="1H goals" value={Num(g.avg_ht_goals)} />
          <Stat label="2H goals" value={Num(g.avg_2h_goals)} />
          <Stat label="Home goals" value={Num(g.avg_home_goals)} />
          <Stat label="Away goals" value={Num(g.avg_away_goals)} />
          <Stat label="Home edge" value={Num(g.home_edge_index)} color={g.home_edge_index > 0.2 ? "green" : "neutral"} />
        </div>
      </Section>
      <Section title="Outcomes">
        <div className="grid grid-cols-3 gap-2">
          <Stat label="Home win" value={Pct(o.home_win_pct)} color="green" />
          <Stat label="Draw" value={Pct(o.draw_pct)} />
          <Stat label="Away win" value={Pct(o.away_win_pct)} color="blue" />
        </div>
      </Section>
      <Section title="Market Frequencies">
        <div className="grid grid-cols-3 gap-2">
          <Stat label="BTTS" value={Pct(m.btts_pct)} />
          <Stat label="Over 2.5" value={Pct(m.over_2_5_pct)} />
          <Stat label="Under 2.5" value={Pct(m.under_2_5_pct)} />
          <Stat label="Over 1.5" value={Pct(m.over_1_5_pct)} />
          <Stat label="Over 3.5" value={Pct(m.over_3_5_pct)} />
          <Stat label="Home CS" value={Pct(m.home_clean_sheet_pct)} />
        </div>
      </Section>
      {p.offense && (
        <div className="flex flex-wrap gap-1.5">
          <Tag label={p.offense} tone={p.offense === "high_scoring" ? "green" : "neutral"} />
          <Tag label={p.btts} tone="blue" />
          <Tag label={p.home_field} tone="amber" />
        </div>
      )}
    </div>
  );
};

const RenderL3 = ({ data }) => {
  const o = data.overall || {};
  const sp = data.home_away_splits || {};
  const fm = data.form_momentum || {};
  const rh = data.recent_history || {};
  const rowsHA = ["home", "away"].map((side) => {
    const r = sp[side] || {};
    return [side.toUpperCase(), r.played ?? "—", r.wins ?? "—", r.draws ?? "—", r.losses ?? "—", r.gf ?? "—", r.ga ?? "—", r.ppg ?? "—", r.win_pct ? Pct(r.win_pct) : "—"];
  });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <Stat label="ELO" value={Num(o.elo, 1)} color="blue" />
        <Stat label="Attack" value={Num(o.attack_rating, 1)} color={o.attack_rating >= 50 ? "green" : "neutral"} />
        <Stat label="Defense" value={Num(o.defense_rating, 1)} color={o.defense_rating >= 50 ? "green" : "neutral"} />
        <Stat label="GF/match" value={Num(o.gf_per_match)} />
        <Stat label="GA/match" value={Num(o.ga_per_match)} />
        <Stat label="BTTS hit" value={Pct(o.btts_hit_pct)} />
      </div>
      <Section title="Home / Away Split">
        <Table headers={["Side", "Played", "W", "D", "L", "GF", "GA", "PPG", "Win%"]} rows={rowsHA} />
      </Section>
      <Section title="Form Momentum">
        <div className="grid grid-cols-3 gap-2">
          <Stat label="Last-5 PPG" value={Num(fm.last_5_ppg)} />
          <Stat label="Prev-5 PPG" value={Num(fm.previous_5_ppg)} />
          <div>
            <Tag label={fm.trend || "—"} tone={fm.trend === "rising" ? "green" : fm.trend === "falling" ? "red" : "neutral"} />
          </div>
        </div>
      </Section>
      {rh.sample_size > 0 && (
        <div className="text-[11px] text-amber-500/70">
          Recent history: <b>{rh.wins}W</b> · <b>{rh.draws}D</b> · <b>{rh.losses}L</b> over {rh.sample_size} matches
        </div>
      )}
    </div>
  );
};

const RenderL6 = ({ data }) => {
  const cs = data.conceded_split || {};
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Goals allowed" value={Num(data.goals_conceded_per_match)} color="red" />
        <Stat label="Defence rating" value={Num(data.defence_rating, 1)} color={data.defence_rating >= 60 ? "green" : "red"} />
        <Stat label="Opp xG faced" value={Num(data.opponent_xg_faced_per_match, 2)} />
      </div>
      <Section title="Conceded Split">
        <div className="grid grid-cols-3 gap-2">
          <Stat label="1H allowed" value={Num(cs.first_half_avg)} />
          <Stat label="2H allowed" value={Num(cs.second_half_avg)} color={cs.second_half_avg > cs.first_half_avg ? "red" : "green"} />
          <Stat label="2H leak ratio" value={cs.second_half_leak_ratio ? `${cs.second_half_leak_ratio}x` : "—"} />
        </div>
      </Section>
      {data.opponent_shots_per_match !== undefined && (
        <Section title="Opponent Attacks Allowed">
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Shots" value={Num(data.opponent_shots_per_match)} />
            <Stat label="SoT" value={Num(data.opponent_sot_per_match)} />
            <Stat label="Big chances" value={Num(data.opponent_big_chances_per_match)} />
          </div>
        </Section>
      )}
    </div>
  );
};

const RenderL7 = ({ data }) => {
  const gf = data.goals_for || {};
  const ga = data.goals_against || {};
  const fg = data.fine_grained_buckets_for;
  return (
    <div className="space-y-3">
      <div className="text-[11px] text-amber-500/70">Sample size: <b>{data.sample_size}</b></div>
      <Section title="Goals For">
        <div className="grid grid-cols-3 gap-2">
          <Stat label="1H per match" value={Num(gf.first_half_per_match)} />
          <Stat label="2H per match" value={Num(gf.second_half_per_match)} />
          <div><Tag label={gf.bias || "—"} tone={gf.bias?.includes("second") ? "green" : "amber"} /></div>
          <Stat label="Matches scored 1H" value={Pct(gf.match_pct_scored_1h)} />
          <Stat label="Matches scored 2H" value={Pct(gf.match_pct_scored_2h)} />
        </div>
      </Section>
      <Section title="Goals Against">
        <div className="grid grid-cols-3 gap-2">
          <Stat label="1H allowed" value={Num(ga.first_half_per_match)} color="red" />
          <Stat label="2H allowed" value={Num(ga.second_half_per_match)} color="red" />
          <div><Tag label={ga.bias || "—"} tone={ga.bias?.includes("second") ? "red" : "neutral"} /></div>
        </div>
      </Section>
      {fg && Object.values(fg).some((v) => v > 0) && (
        <Section title="Fine-grained Buckets (goals for)">
          <div className="grid grid-cols-6 gap-1.5">
            {Object.entries(fg).map(([bucket, count]) => (
              <Stat key={bucket} label={bucket} value={count} color={count > 0 ? "green" : "neutral"} />
            ))}
          </div>
        </Section>
      )}
    </div>
  );
};

const RenderL8 = ({ data }) => {
  const si = data.style_indicators || {};
  const ad = data.in_match_adaptability || {};
  const disc = data.discipline || {};
  const cf = data.current_form || {};
  return (
    <div className="space-y-3">
      {cf.form_string && (
        <Section title="Current Form">
          <div className="flex items-center gap-3">
            <div className="font-mono text-2xl tracking-widest font-bold text-amber-300">{cf.form_string}</div>
            <div className="text-[11px] text-amber-500/70">Position <b>#{cf.league_position || "?"}</b></div>
          </div>
        </Section>
      )}
      {si.avg_possession_pct !== undefined && (
        <Section title="Style">
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Possession" value={Pct(si.avg_possession_pct)} />
            <Stat label="Passing acc" value={Pct(si.avg_passing_accuracy_pct)} />
            <div><Tag label={si.style_profile || "—"} tone={si.style_profile === "possession_dominant" ? "green" : "amber"} /></div>
          </div>
        </Section>
      )}
      {ad.avg_formation_changes !== undefined && (
        <Section title="In-Match Adaptability">
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Formation Δ" value={Num(ad.avg_formation_changes)} />
            <Stat label="Attk subs" value={Num(ad.avg_attacking_subs)} />
            <Stat label="Def subs" value={Num(ad.avg_defensive_subs)} />
          </div>
          <div className="mt-2"><Tag label={ad.coach_reactivity || "—"} tone="blue" /></div>
        </Section>
      )}
      {disc.avg_yellow_cards !== undefined && (
        <div className="grid grid-cols-2 gap-2">
          <Stat label="Yellow cards" value={Num(disc.avg_yellow_cards)} />
          <Stat label="Red cards" value={Num(disc.avg_red_cards)} color="red" />
        </div>
      )}
    </div>
  );
};

const RenderL9 = ({ data }) => {
  const ob = data.outcome_bias || {};
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="text-lg font-bold text-amber-200">{data.referee}</div>
        {data.sample_size !== undefined && <div className="text-[11px] text-amber-500/70">· {data.sample_size} matches indexed</div>}
      </div>
      {data.avg_total_goals !== undefined && (
        <div className="grid grid-cols-2 gap-2">
          <Stat label="Avg total goals" value={Num(data.avg_total_goals)} />
          <Stat label="Indexed fixtures" value={data.indexed_fixtures} />
        </div>
      )}
      {ob.home_win_pct !== undefined && (
        <Section title="Outcome Bias">
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Home %" value={Pct(ob.home_win_pct)} color="green" />
            <Stat label="Draw %" value={Pct(ob.draw_pct)} />
            <Stat label="Away %" value={Pct(ob.away_win_pct)} color="blue" />
          </div>
          {ob.tag && <div className="mt-2"><Tag label={ob.tag} tone={ob.tag.includes("home") ? "green" : "amber"} /></div>}
        </Section>
      )}
    </div>
  );
};

const RenderL10 = ({ data }) => {
  return (
    <div className="space-y-3">
      <div className="text-lg font-bold text-amber-200">{data.venue}</div>
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Home win %" value={Pct(data.home_win_pct)} color="green" />
        <Stat label="Draw %" value={Pct(data.draw_pct)} />
        <Stat label="Total goals" value={Num(data.avg_total_goals)} />
      </div>
      {data.primary_hosts?.length > 0 && (
        <Section title="Primary Hosts">
          <Table
            headers={["Team", "Matches", "Wins", "Home Win %"]}
            rows={data.primary_hosts.map((h) => [h.name, h.n, h.w, Pct(h.home_win_pct_at_venue)])}
          />
        </Section>
      )}
    </div>
  );
};

const RenderL11 = ({ data }) => {
  const v = data.venue || {};
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div className="col-span-2">
          <div className="text-[10px] uppercase tracking-widest text-amber-500/60">Venue</div>
          <div className="text-sm font-semibold text-amber-100">{v.name || "—"}{v.city && ` · ${v.city}`}</div>
        </div>
        {data.referee && <Stat label="Referee" value={data.referee} />}
        {data.timezone && <Stat label="Timezone" value={data.timezone} />}
      </div>
      {data.weather ? (
        <Section title="Weather">
          <KVList items={Object.entries(data.weather)} />
        </Section>
      ) : (
        <Note>{data.note}</Note>
      )}
      {data.expected_impact_factors?.length > 0 && (
        <Section title="Expected Impact">
          <div className="flex flex-wrap gap-1.5">
            {data.expected_impact_factors.map((f, i) => <Tag key={i} label={f} tone={f === "conditions_neutral" ? "neutral" : "red"} />)}
          </div>
        </Section>
      )}
    </div>
  );
};

const RenderL13 = ({ data }) => {
  const cs = data.current_streak || {};
  const bb = data.bounce_back || {};
  const lc = data.league_context || {};
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Streak type" value={cs.type || "—"} color={cs.type === "W" ? "green" : cs.type === "L" ? "red" : "neutral"} />
        <Stat label="Streak length" value={cs.length ?? "—"} />
        <div><Tag label={cs.state || "neutral"} tone={cs.state === "confident_surge" ? "green" : cs.state === "fragile_slump" ? "red" : "amber"} /></div>
      </div>
      <Section title="Bounce Back">
        <div className="grid grid-cols-3 gap-2">
          <Stat label="Opportunities" value={bb.opportunities ?? "—"} />
          <Stat label="Successful" value={bb.successful ?? "—"} color="green" />
          <Stat label="Rate" value={bb.rate_pct !== null ? Pct(bb.rate_pct) : "—"} />
        </div>
      </Section>
      {lc.position && (
        <Section title="League Context">
          <div className="text-[11px] text-amber-100/90 mb-2">
            Position <b>#{lc.position}</b> of {lc.total_teams}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(lc.tags || []).map((t, i) => (
              <div key={i} className="rounded border border-amber-500/25 bg-amber-500/10 px-2 py-1 text-[10px]">
                <div className="text-amber-300 font-semibold uppercase tracking-widest">{t.tag}</div>
                <div className="text-amber-100/70">{t.description}</div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
};

const RenderL14 = ({ data }) => {
  const s = data.strongest_edge || {};
  return (
    <div className="space-y-3">
      <div className="text-sm font-bold text-amber-200">{data.league_name}</div>
      <div className="text-[11px] text-amber-500/70">Discovered <b>{data.total_patterns}</b> patterns</div>
      {s.market && (
        <div className="rounded-lg border-2 border-amber-400/50 bg-amber-500/10 p-3">
          <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-1">Strongest Edge</div>
          <div className="text-sm font-bold text-amber-100">{s.market}</div>
          <div className="text-[11px] text-amber-100/80">Conf bucket <b>{s.bucket}</b> · actual hit <b>{Pct(s.actual_hit_rate)}</b> · delta <b>+{Num(s.delta_pp, 1)}pp</b> · n={s.samples}</div>
        </div>
      )}
      {data.positive_edges_top15?.length > 0 && (
        <Section title="Positive Edges (top 8)">
          <Table
            headers={["Market", "Bucket", "Samples", "Hit rate", "Δpp"]}
            rows={data.positive_edges_top15.slice(0, 8).map((r) => [r.market, r.bucket, r.samples, Pct(r.actual_hit_rate), `+${Num(r.delta_pp, 1)}`])}
          />
        </Section>
      )}
      {data.avoid_traps_top15?.length > 0 && (
        <Section title="Avoid Traps (top 8)">
          <Table
            headers={["Market", "Bucket", "Samples", "Hit rate", "Δpp"]}
            rows={data.avoid_traps_top15.slice(0, 8).map((r) => [r.market, r.bucket, r.samples, Pct(r.actual_hit_rate), Num(r.delta_pp, 1)])}
          />
        </Section>
      )}
    </div>
  );
};

const RenderL15 = ({ data }) => {
  const s = data.strongest_market_edge || {};
  return (
    <div className="space-y-3">
      <div className="text-sm font-bold text-amber-200">{data.team_name}</div>
      <div className="text-[11px] text-amber-500/70">Discovered <b>{data.total_patterns}</b> patterns</div>
      {s.market && (
        <div className="rounded-lg border-2 border-amber-400/50 bg-amber-500/10 p-3">
          <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-1">Strongest Market Edge</div>
          <div className="text-sm font-bold text-amber-100">{s.market}</div>
          <div className="text-[11px] text-amber-100/80">Actual hit <b>{Pct(s.actual_hit_rate)}</b> · delta <b>+{Num(s.delta_pp, 1)}pp</b> · n={s.samples}</div>
        </div>
      )}
      {data.team_outperforms_in?.length > 0 && (
        <Section title="Team Outperforms In">
          <Table
            headers={["Market", "Samples", "Hit rate", "Δpp"]}
            rows={data.team_outperforms_in.slice(0, 8).map((r) => [r.market, r.samples, Pct(r.actual_hit_rate), `+${Num(r.delta_pp, 1)}`])}
          />
        </Section>
      )}
    </div>
  );
};

const RenderL16 = ({ data }) => {
  const sd = data.star_dependence || {};
  return (
    <div className="space-y-3">
      {data.top_scorer && (
        <Section title="Top Scorer">
          <div className="flex items-center gap-3">
            {data.top_scorer.photo && <img src={data.top_scorer.photo} alt="" className="w-10 h-10 rounded-full border-2 border-amber-400" />}
            <div>
              <div className="text-sm font-bold text-amber-100">{data.top_scorer.name}</div>
              <div className="text-[11px] text-amber-500/70">{data.top_scorer.goals} goals</div>
            </div>
          </div>
        </Section>
      )}
      {sd.top_scorer_goal_share_pct !== null && sd.top_scorer_goal_share_pct !== undefined && (
        <Section title="Star Dependence">
          <div className="flex items-center gap-3">
            <Stat label="Top scorer share" value={Pct(sd.top_scorer_goal_share_pct)} />
            <Tag label={sd.risk_flag || "—"} tone={sd.risk_flag === "high_star_dependence" ? "red" : sd.risk_flag === "balanced_attack" ? "green" : "amber"} />
          </div>
        </Section>
      )}
      {data.known_scorers?.length > 0 && (
        <Section title={`Known Scorers (${data.known_scorers.length})`}>
          <div className="grid grid-cols-2 gap-1.5">
            {data.known_scorers.slice(0, 8).map((p, i) => (
              <div key={i} className="flex items-center gap-2 rounded border border-amber-500/15 bg-black/40 p-1.5">
                {p.photo && <img src={p.photo} alt="" className="w-7 h-7 rounded-full" />}
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold text-amber-100 truncate">{p.name}</div>
                  <div className="text-[10px] text-amber-500/70">{p.goals} goals · S{p.season}</div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
};

const RenderL17 = ({ data }) => {
  const impl = data.implied_probs_pct || {};
  const fair = data.fair_probs_pct || {};
  const edges = data.edges_pp || {};
  const vp = data.value_pick;
  return (
    <div className="space-y-3">
      <Section title="Implied Probabilities (raw)">
        <div className="grid grid-cols-3 gap-2">
          <Stat label="Home" value={impl.home !== null ? Pct(impl.home) : "—"} />
          <Stat label="Draw" value={impl.draw !== null ? Pct(impl.draw) : "—"} />
          <Stat label="Away" value={impl.away !== null ? Pct(impl.away) : "—"} />
        </div>
      </Section>
      {fair.home !== undefined && (
        <Section title={`Fair Probabilities · Margin ${Num(data.bookmaker_margin_pct)}%`}>
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Home" value={Pct(fair.home)} color="green" />
            <Stat label="Draw" value={Pct(fair.draw)} />
            <Stat label="Away" value={Pct(fair.away)} color="blue" />
          </div>
        </Section>
      )}
      {edges.home !== undefined && (
        <Section title="Model vs Market Edge (pp)">
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Home" value={edges.home > 0 ? `+${edges.home}` : edges.home} color={edges.home > 3 ? "green" : "neutral"} />
            <Stat label="Draw" value={edges.draw > 0 ? `+${edges.draw}` : edges.draw} color={edges.draw > 3 ? "green" : "neutral"} />
            <Stat label="Away" value={edges.away > 0 ? `+${edges.away}` : edges.away} color={edges.away > 3 ? "green" : "neutral"} />
          </div>
        </Section>
      )}
      {vp && (
        <div className="rounded-lg border-2 border-green-400/50 bg-green-500/10 p-3">
          <div className="text-[10px] uppercase tracking-widest text-green-400 mb-1">💎 Value Pick</div>
          <div className="text-sm font-bold text-green-100 capitalize">{vp.side} · edge +{vp.edge_pp}pp</div>
          <div className="text-[11px] text-green-100/70">Market odds {vp.market_odds} · fair {vp.fair_odds}</div>
        </div>
      )}
    </div>
  );
};

const RenderH2H = ({ data }) => {
  const rec = data.record || {};
  const g = data.goals || {};
  const mf = data.market_frequencies || {};
  return (
    <div className="space-y-3">
      <div className="text-[11px] text-amber-500/70">Meetings: <b>{data.meetings}</b></div>
      <Section title="Head-to-Head Record">
        <div className="grid grid-cols-3 gap-2">
          <Stat label="A wins" value={rec.team_a_wins} color="green" />
          <Stat label="Draws" value={rec.draws} />
          <Stat label="B wins" value={rec.team_b_wins} color="red" />
        </div>
      </Section>
      <Section title="Goals & Markets">
        <div className="grid grid-cols-3 gap-2">
          <Stat label="A goals" value={g.team_a_total} />
          <Stat label="B goals" value={g.team_b_total} />
          <Stat label="Total avg" value={Num(g.avg_total_per_match)} />
          <Stat label="BTTS%" value={Pct(mf.btts_pct)} />
          <Stat label="Over 2.5%" value={Pct(mf.over_2_5_pct)} />
          <Stat label="Dominance" value={data.dominance_score !== undefined ? `${data.dominance_score > 0 ? "+" : ""}${data.dominance_score}` : "—"} color={data.dominance_score > 30 ? "green" : data.dominance_score < -30 ? "red" : "neutral"} />
        </div>
      </Section>
      {data.last_5_meetings?.length > 0 && (
        <Section title="Last 5 Meetings">
          <Table
            headers={["Date", "Home", "Away", "Score", "Venue"]}
            rows={data.last_5_meetings.map((m) => [(m.date || "").slice(0, 10), m.home, m.away, m.score, m.venue])}
          />
        </Section>
      )}
    </div>
  );
};

const RenderL18 = ({ data }) => {
  // Iter 259 — L18 payload shape:
  //   { output: { fixture_id, inputs, sub_models, markets: { 1x2, double_chance, ... }, top_factors, ... }, sub_models_active }
  const out = data.output || {};
  const mk = out.markets || {};
  if (!mk["1x2"]) {
    return (
      <div className="space-y-2">
        <Note>Ensemble engine returned no market predictions.</Note>
        {data.sub_models_active?.length > 0 && (
          <Section title="Active Sub-models">
            <div className="flex flex-wrap gap-1">
              {data.sub_models_active.map((s, i) => <Tag key={i} label={s} tone="blue" />)}
            </div>
          </Section>
        )}
      </div>
    );
  }
  const one = mk["1x2"];
  const p = one.probs || {};
  return (
    <div className="space-y-3">
      {/* Headline prediction */}
      <div className="rounded-lg border-2 border-amber-400/50 bg-gradient-to-r from-amber-900/40 to-black p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[10px] uppercase tracking-widest text-amber-500/70">Prediction</div>
          <div className="text-lg font-bold text-amber-100 flex-1">{one.prediction}</div>
          <Tag label={one.risk_rating || "—"} tone={one.risk_rating === "low" ? "green" : one.risk_rating === "high" ? "red" : "amber"} />
        </div>
        <div className="grid grid-cols-3 gap-2 mt-2">
          <Stat label="Confidence" value={`${Num(one.confidence, 1)}%`} color="green" />
          <Stat label="Value" value={one.value_score_pp !== null && one.value_score_pp !== undefined ? `${one.value_score_pp > 0 ? "+" : ""}${Num(one.value_score_pp, 1)}pp` : "—"} color={one.value_score_pp > 3 ? "green" : "neutral"} />
          <Stat label="Risk" value={one.risk_rating || "—"} />
        </div>
        <div className="text-[10px] text-amber-100/80 mt-2 font-mono">
          H {Num((p.home || 0) * 100, 1)}% · D {Num((p.draw || 0) * 100, 1)}% · A {Num((p.away || 0) * 100, 1)}%
        </div>
      </div>

      {/* Sub-model votes */}
      {out.sub_models && (
        <Section title="Sub-model Votes">
          <div className="grid grid-cols-2 gap-1.5">
            {Object.entries(out.sub_models).filter(([, v]) => v && typeof v === "object").map(([name, sm]) => (
              <div key={name} className="text-[10px] rounded border border-amber-500/15 bg-black/40 px-2 py-1">
                <div className="uppercase tracking-widest text-amber-500/70">{name.replace(/_/g, " ")}</div>
                <div className="font-mono text-amber-100">H{Num((sm.home || 0) * 100, 0)} · D{Num((sm.draw || 0) * 100, 0)} · A{Num((sm.away || 0) * 100, 0)}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Compact market grid */}
      <Section title="20+ Market Predictions">
        <div className="grid grid-cols-2 gap-1.5 text-[10px]">
          {mk.double_chance && (
            <div className="rounded border border-amber-500/15 bg-black/40 px-2 py-1">
              <div className="uppercase tracking-widest text-amber-500/70">Double Chance</div>
              <div className="text-amber-100 font-mono">{mk.double_chance.recommended} · {Num((mk.double_chance[mk.double_chance.recommended] || 0) * 100, 1)}%</div>
            </div>
          )}
          {mk.draw_no_bet && (
            <div className="rounded border border-amber-500/15 bg-black/40 px-2 py-1">
              <div className="uppercase tracking-widest text-amber-500/70">Draw No Bet</div>
              <div className="text-amber-100 font-mono">{mk.draw_no_bet.recommended} · {Num((mk.draw_no_bet[mk.draw_no_bet.recommended] || 0) * 100, 1)}%</div>
            </div>
          )}
          {mk.btts && (
            <div className="rounded border border-amber-500/15 bg-black/40 px-2 py-1">
              <div className="uppercase tracking-widest text-amber-500/70">BTTS Yes</div>
              <div className="text-amber-100 font-mono">{Num(mk.btts.yes * 100, 1)}%</div>
            </div>
          )}
          {mk.over_under?.over_2_5 !== undefined && (
            <div className="rounded border border-amber-500/15 bg-black/40 px-2 py-1">
              <div className="uppercase tracking-widest text-amber-500/70">Over 2.5</div>
              <div className="text-amber-100 font-mono">{Num(mk.over_under.over_2_5 * 100, 1)}%</div>
            </div>
          )}
          {mk.over_under?.over_1_5 !== undefined && (
            <div className="rounded border border-amber-500/15 bg-black/40 px-2 py-1">
              <div className="uppercase tracking-widest text-amber-500/70">Over 1.5</div>
              <div className="text-amber-100 font-mono">{Num(mk.over_under.over_1_5 * 100, 1)}%</div>
            </div>
          )}
          {mk.over_under?.over_3_5 !== undefined && (
            <div className="rounded border border-amber-500/15 bg-black/40 px-2 py-1">
              <div className="uppercase tracking-widest text-amber-500/70">Over 3.5</div>
              <div className="text-amber-100 font-mono">{Num(mk.over_under.over_3_5 * 100, 1)}%</div>
            </div>
          )}
          {mk.clean_sheet && (
            <div className="rounded border border-amber-500/15 bg-black/40 px-2 py-1">
              <div className="uppercase tracking-widest text-amber-500/70">Home CS</div>
              <div className="text-amber-100 font-mono">{Num(mk.clean_sheet.home * 100, 1)}%</div>
            </div>
          )}
          {mk.clean_sheet && (
            <div className="rounded border border-amber-500/15 bg-black/40 px-2 py-1">
              <div className="uppercase tracking-widest text-amber-500/70">Away CS</div>
              <div className="text-amber-100 font-mono">{Num(mk.clean_sheet.away * 100, 1)}%</div>
            </div>
          )}
          {mk.first_team_to_score && (
            <div className="rounded border border-amber-500/15 bg-black/40 px-2 py-1">
              <div className="uppercase tracking-widest text-amber-500/70">First to Score</div>
              <div className="text-amber-100 font-mono">
                {mk.first_team_to_score.home > mk.first_team_to_score.away ? "Home" : "Away"}
                {" "}· {Num(Math.max(mk.first_team_to_score.home, mk.first_team_to_score.away) * 100, 1)}%
              </div>
            </div>
          )}
          {mk.half_time_result && (
            <div className="rounded border border-amber-500/15 bg-black/40 px-2 py-1">
              <div className="uppercase tracking-widest text-amber-500/70">HT Result</div>
              <div className="text-amber-100 font-mono">
                H{Num(mk.half_time_result.home * 100, 0)}·D{Num(mk.half_time_result.draw * 100, 0)}·A{Num(mk.half_time_result.away * 100, 0)}
              </div>
            </div>
          )}
        </div>
      </Section>

      {/* Correct scores */}
      {mk.correct_score_top5?.length > 0 && (
        <Section title="Correct Score (Top 5)">
          <div className="flex flex-wrap gap-1.5">
            {mk.correct_score_top5.map((cs, i) => (
              <div key={i} className="text-[11px] px-2 py-1 rounded border border-amber-500/20 bg-black/50 font-mono">
                <b>{cs.score}</b> <span className="text-amber-500/70">{Num(cs.prob * 100, 1)}%</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Top factors */}
      {out.top_factors?.length > 0 && (
        <Section title="Top Factors">
          <div className="flex flex-wrap gap-1">
            {out.top_factors.map((f, i) => (
              <div key={i} className="text-[10px] px-2 py-0.5 rounded border border-amber-500/20 bg-amber-500/5">
                <span className="text-amber-500/70">{f.factor}</span> · <b className="text-amber-100">{f.value}</b> → <span className="text-amber-300">{f.direction}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Inputs */}
      {out.inputs && (
        <div className="text-[10px] text-amber-500/70 font-mono border-t border-amber-500/10 pt-2">
          xG {out.inputs.home_xg}—{out.inputs.away_xg} · Elo {out.inputs.elo_home} vs {out.inputs.elo_away} · League avg {out.inputs.league_avg_goals}
        </div>
      )}
    </div>
  );
};

// ─── Router ────────────────────────────────────────────────────
const RENDERERS = {
  1: RenderL1, 2: RenderL2, 3: RenderL3, 6: RenderL6, 7: RenderL7,
  8: RenderL8, 9: RenderL9, 10: RenderL10, 11: RenderL11, 13: RenderL13,
  14: RenderL14, 15: RenderL15, 16: RenderL16, 17: RenderL17, 18: RenderL18,
  h2h: RenderH2H,
};

// Generic fallback for layers we don't have a bespoke renderer for
const RenderGeneric = ({ data }) => {
  if (data.status && !data.sample_size && !data.total_patterns) {
    return (
      <div className="space-y-2">
        <StatusBadge status={data.status} />
        {data.note && <Note>{data.note}</Note>}
        {data.collecting?.length > 0 && (
          <Section title="Collecting">
            <div className="flex flex-wrap gap-1">
              {data.collecting.map((c, i) => <Tag key={i} label={c} tone="neutral" />)}
            </div>
          </Section>
        )}
      </div>
    );
  }
  // Fallback: show top-level scalar fields as stat cards, excluding known metadata
  const HIDDEN = ["layer", "title", "collecting", "note", "cached", "fixture_id",
    "team_id", "league_id", "venue", "referee", "status"];
  const scalars = Object.entries(data).filter(([k, v]) =>
    !HIDDEN.includes(k) &&
    (typeof v === "number" || typeof v === "string" || typeof v === "boolean")
  );
  // Also collect nested dicts as sub-sections
  const nested = Object.entries(data).filter(([k, v]) =>
    !HIDDEN.includes(k) && v && typeof v === "object" && !Array.isArray(v)
  );
  return (
    <div className="space-y-3">
      {scalars.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {scalars.slice(0, 10).map(([k, v]) => (
            <Stat key={k} label={k.replace(/_/g, " ")} value={typeof v === "number" ? Num(v) : String(v)} />
          ))}
        </div>
      )}
      {nested.slice(0, 4).map(([k, obj]) => {
        const inner = Object.entries(obj).filter(([, v]) =>
          typeof v === "number" || typeof v === "string" || typeof v === "boolean"
        );
        if (inner.length === 0) return null;
        return (
          <Section key={k} title={k.replace(/_/g, " ")}>
            <KVList items={inner.map(([ik, iv]) => [ik.replace(/_/g, " "), typeof iv === "number" ? Num(iv) : String(iv)])} />
          </Section>
        );
      })}
      {data.note && <Note>{data.note}</Note>}
      {data.collecting?.length > 0 && (
        <Section title="Collecting">
          <div className="flex flex-wrap gap-1">
            {data.collecting.map((c, i) => <Tag key={i} label={c} tone="neutral" />)}
          </div>
        </Section>
      )}
    </div>
  );
};

export default function GoldLayerRenderer({ layerId, data }) {
  if (!data) return null;
  // Layers can also have a top-level status/no_data — show it first
  const Renderer = RENDERERS[layerId] || RenderGeneric;
  return (
    <div data-testid={`gold-layer-${layerId}-rendered`}>
      {data.status && data.status !== "ok" && ["no_data", "not_indexed_yet", "not_available", "insufficient_data", "no_calibration_data", "unknown_team", "unknown_league", "no_shots_data", "coming_soon"].includes(data.status) ? (
        <div className="space-y-2">
          <StatusBadge status={data.status} />
          {data.note && <Note>{data.note}</Note>}
          {data.collecting?.length > 0 && (
            <Section title="Collecting">
              <div className="flex flex-wrap gap-1">
                {data.collecting.map((c, i) => <Tag key={i} label={c} tone="neutral" />)}
              </div>
            </Section>
          )}
        </div>
      ) : (
        <Renderer data={data} />
      )}
    </div>
  );
}
